import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ShieldCheck, ExternalLink } from "lucide-react";
import { MARKETPLACE_BRAND } from "../../data/marketplaceData";
import { FOOTER_MASTER_LOGO } from "../TritalLogo";

const FOOTER_NAV = [
  {
    title: "Marketplace",
    links: [
      { to: "/shifts",            label: "Home" },
      { to: "/shifts/browse",     label: "Browse Shifts" },
      { to: "/shifts/facilities", label: "For Facilities" },
      { to: "/shifts/clinicians", label: "For Clinicians" },
      { to: "/shifts/faq",        label: "FAQ" },
      { to: "/shifts/contact",    label: "Support" },
    ],
  },
  {
    title: "Get started",
    links: [
      { to: "/shifts/signup/facility",  label: "Facility signup" },
      { to: "/shifts/signup/clinician", label: "Clinician signup" },
      { to: "/shifts/login",            label: "Sign in" },
    ],
  },
  {
    title: "TRITAL Care®",
    links: [
      { to: "/", label: "Company site", external: true },
      { to: "/compliance", label: "Compliance" },
      { to: "/diversity-and-inclusion", label: "Diversity & Inclusion" },
      { to: "/about", label: "About" },
      { to: "/blog", label: "Resources" },
    ],
  },
  {
    title: "Legal",
    links: [
      { to: "/legal/privacy", label: "Privacy" },
      { to: "/legal/terms", label: "Terms" },
      { to: "/legal/cookies", label: "Cookies" },
      { to: "/legal/accessibility", label: "Accessibility" },
    ],
  },
];

export default function ShiftsFooter() {
  return (
    <footer className="bg-trital-navy-ink text-white relative overflow-hidden mt-24" data-testid="shifts-footer">
      <div className="absolute inset-0 bg-trital-navy-mesh opacity-80 pointer-events-none" />
      <div className="absolute inset-0 dot-grid opacity-10 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1.4fr_3fr] gap-12">
          {/* Brand column */}
          <div>
            <Link to="/" aria-label="TRITAL Care — home" className="inline-block -ml-3 lg:-ml-5" data-testid="shifts-footer-logo">
              <img
                src={FOOTER_MASTER_LOGO}
                alt="TRITAL Care® — Triage Precision. Trusted Workforce."
                className="h-36 md:h-44 lg:h-52 w-auto select-none drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)]"
                draggable={false}
              />
            </Link>
            <div className="leading-none -mt-2">
              <div className="font-display font-black text-[16px] text-white">{MARKETPLACE_BRAND.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/55 mt-1">{MARKETPLACE_BRAND.byline}</div>
            </div>
            <p className="mt-5 text-white/70 text-[14px] leading-relaxed max-w-[40ch]">{MARKETPLACE_BRAND.tagline} Built and operated by TRITAL Care® — the workforce partner for hospitals and health systems.</p>

            <ul className="mt-6 space-y-2.5 text-[13px] text-white/80">
              <li className="inline-flex items-start gap-2">
                <Phone className="w-3.5 h-3.5 mt-0.5 text-trital-red" strokeWidth={1.8} />
                <a href="sms:+18323441525" className="hover:text-white" data-testid="shifts-footer-phone">Text recruiter: (832) 344-1525</a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="w-3.5 h-3.5 mt-0.5 text-trital-red" strokeWidth={1.8} />
                <a href="mailto:shifts@tritalcare.com" className="hover:text-white" data-testid="shifts-footer-email">shifts@tritalcare.com</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 mt-0.5 text-trital-red" strokeWidth={1.8} />
                <span>5423 Pecan Fall PL, Missouri City, TX 77459</span>
              </li>
              <li className="flex items-start gap-2 pt-2">
                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-trital-red" strokeWidth={1.8} />
                <span className="text-white/65">NMSDC · HMSDC · NCTRCA-MBE · SBE · SAM.gov</span>
              </li>
            </ul>
          </div>

          {/* Link grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {FOOTER_NAV.map((section) => (
              <div key={section.title}>
                <div className="font-display font-bold text-[13px] tracking-wide text-white">{section.title}</div>
                <ul className="mt-4 space-y-2.5">
                  {section.links.map((l) => (
                    <li key={l.to + l.label}>
                      <Link
                        to={l.to}
                        className="text-[13px] text-white/70 hover:text-white inline-flex items-center gap-1"
                      >
                        {l.label}
                        {l.external && <ExternalLink className="w-3 h-3 opacity-60" strokeWidth={1.8} />}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] text-white/55">
          <div>© {new Date().getFullYear()} TRITAL Care®. All rights reserved.</div>
          <div>
            TRITAL Care® is the operating brand name of <span className="text-white/80">TOPTAL Care Inc.</span>, a Texas corporation.
          </div>
        </div>
      </div>
    </footer>
  );
}
